import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { VideoThumbnailGenerator } from 'browser-video-thumbnail-generator';
import { apiHandler } from '../../../api/apihandler';
import { FEED_POST, FEED_MEDIA_POST, REG_OPTIONS_DETAILS } from '../../../api/constants';
import { handleImageUpload } from '../../../api/uploads';
import { parseOption, apiSucceeded, apiErrorMessage } from '../../../views/dashboard/auth/onboarding/apiUtils';
import './create-recipe-page.css';

// Web port of the mobile app's CreateRecipeScreen - same FEED_POST /
// FEED_MEDIA_POST two-phase submit (create post, upload media, finalize)
// and the same structured recipe payload (title, ingredients, steps, tips,
// cook_time, calorie_count, serving_for, cuisine_ids).

const emptyIngredient = () => ({ id: Date.now() + Math.random(), amount: '', item: '' });
const emptyStep = () => ({ id: Date.now() + Math.random(), instruction: '' });
const emptyTip = () => ({ id: Date.now() + Math.random(), tip_text: '' });

const CreateRecipePage = () => {
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [cookTime, setCookTime] = useState('');
  const [calories, setCalories] = useState('');
  const [servings, setServings] = useState('');

  const [cuisines, setCuisines] = useState([]);
  const [cuisinesLoading, setCuisinesLoading] = useState(true);
  const [selectedCuisines, setSelectedCuisines] = useState([]);

  const [ingredients, setIngredients] = useState([emptyIngredient()]);
  const [steps, setSteps] = useState([emptyStep()]);
  const [tips, setTips] = useState([]);

  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState(null);
  const [mediaIsVideo, setMediaIsVideo] = useState(false);
  const [thumbFile, setThumbFile] = useState(null);
  const [thumbPreviewUrl, setThumbPreviewUrl] = useState(null);

  const [activeTab, setActiveTab] = useState('ingredients');
  const [isLoading, setIsLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiHandler(REG_OPTIONS_DETAILS, 'GET');
        if (apiSucceeded(res)) {
          const d = res?.data?.data;
          setCuisines((d?.cuisines || []).filter((i) => i.status !== 0).map(parseOption));
        }
      } catch (e) { console.error(e); }
      finally { setCuisinesLoading(false); }
    })();
  }, []);

  const warn = (text) => Swal.fire({ title: 'Required', text, icon: 'warning', confirmButtonText: 'Okay' });

  function handleGenerateThumbnail(videoDataUrl) {
    try {
      const generator = new VideoThumbnailGenerator(videoDataUrl);
      generator.getThumbnail().then((thumbnail) => {
        fetch(thumbnail?.thumbnail)
          .then((res) => res.blob())
          .then((blob) => {
            const img = new Image();
            img.src = URL.createObjectURL(blob);
            img.onload = () => {
              const canvas = document.createElement('canvas');
              canvas.width = 500;
              canvas.height = 300;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0, 500, 300);
              canvas.toBlob((resizedBlob) => {
                const file = new File([resizedBlob], `${Date.now()}.jpg`, { type: 'image/jpeg' });
                setThumbFile(file);
                setThumbPreviewUrl(URL.createObjectURL(resizedBlob));
              }, 'image/jpeg');
            };
          })
          .catch((e) => console.error('Error generating thumbnail:', e));
      });
    } catch (e) {
      console.error(e);
    }
  }

  const handleMediaChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const isVideo = file.type.startsWith('video');
    setMediaFile(file);
    setMediaIsVideo(isVideo);
    setThumbFile(null);
    setThumbPreviewUrl(null);

    const reader = new FileReader();
    reader.onloadend = () => {
      setMediaPreviewUrl(reader.result);
      if (isVideo) handleGenerateThumbnail(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const removeMedia = () => {
    setMediaFile(null);
    setMediaPreviewUrl(null);
    setMediaIsVideo(false);
    setThumbFile(null);
    setThumbPreviewUrl(null);
  };

  const toggleCuisine = (id) => {
    setSelectedCuisines((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));
  };

  const addIngredientRow = () => setIngredients((p) => [...p, emptyIngredient()]);
  const updateIngredient = (id, field, val) => setIngredients((p) => p.map((r) => (r.id === id ? { ...r, [field]: val } : r)));
  const removeIngredient = (id) => setIngredients((p) => p.filter((r) => r.id !== id));

  const addStepRow = () => setSteps((p) => [...p, emptyStep()]);
  const updateStep = (id, val) => setSteps((p) => p.map((r) => (r.id === id ? { ...r, instruction: val } : r)));
  const removeStep = (id) => setSteps((p) => p.filter((r) => r.id !== id));

  const addTipRow = () => setTips((p) => [...p, emptyTip()]);
  const updateTip = (id, val) => setTips((p) => p.map((r) => (r.id === id ? { ...r, tip_text: val } : r)));
  const removeTip = (id) => setTips((p) => p.filter((r) => r.id !== id));

  async function handlePost() {
    if (!title.trim()) return warn('Please enter a recipe title.');
    if (!content.trim()) return warn('Please add a description.');

    const filledIngredients = ingredients.filter((r) => r.item.trim());
    const filledSteps = steps.filter((r) => r.instruction.trim());
    const filledTips = tips.filter((r) => r.tip_text?.trim());

    if (!filledIngredients.length) return warn('Add at least one ingredient.');
    if (!filledSteps.length) return warn('Add at least one step.');
    if (!mediaFile) return warn('Please add a photo or video of your dish.');

    setIsLoading(true);
    setUploadStatus('Creating recipe…');
    try {
      const basePayload = {
        title: title.trim(),
        content: content.trim(),
        cook_time: cookTime ? parseInt(cookTime, 10) : 0,
        calorie_count: calories ? parseInt(calories, 10) : 0,
        serving_for: servings ? parseInt(servings, 10) : 1,
        is_completed: 0,
        cuisine_ids: selectedCuisines,
        ingredients: filledIngredients.map(({ amount, item }) => ({ amount, item })),
        steps: filledSteps.map(({ instruction }, i) => ({ step_number: i + 1, instruction })),
        tips: filledTips.map(({ tip_text }) => ({ tip_text })),
      };

      const res = await apiHandler(FEED_POST, 'POST', basePayload);
      if (!apiSucceeded(res)) {
        Swal.fire('Error', apiErrorMessage(res, 'Failed to post recipe.'), 'error');
        return;
      }

      const postId = res?.data?.data?.feed?.id;

      setUploadStatus('Uploading media to server…');
      const s3Url = await handleImageUpload(mediaFile, mediaIsVideo ? 'feedVideos/' : 'feedImages/', mediaFile.type);

      let thumbS3Url = '';
      if (mediaIsVideo && thumbFile) {
        setUploadStatus('Uploading thumbnail…');
        try {
          thumbS3Url = await handleImageUpload(thumbFile, 'feedVideosThumb/', 'image/jpeg');
        } catch (_) { thumbS3Url = ''; }
      }

      setUploadStatus('Saving media…');
      const mediaRes = await apiHandler(FEED_MEDIA_POST, 'POST', {
        post_id: postId,
        path: s3Url,
        media_type: mediaIsVideo ? '1' : '0',
        thumb: thumbS3Url,
      });
      if (!apiSucceeded(mediaRes)) {
        throw new Error(apiErrorMessage(mediaRes, 'Failed to save media.'));
      }

      setUploadStatus('Finalising recipe…');
      const finalRes = await apiHandler(FEED_POST, 'POST', {
        ...basePayload,
        post_id: postId,
        is_completed: 1,
      });
      if (!apiSucceeded(finalRes)) {
        throw new Error(apiErrorMessage(finalRes, 'Failed to finalise recipe.'));
      }

      await Swal.fire('Posted!', 'Your recipe has been shared.', 'success');
      navigate('/pages/home');
    } catch (err) {
      Swal.fire('Error', err?.message || 'Something went wrong. Please try again.', 'error');
    } finally {
      setIsLoading(false);
      setUploadStatus('');
    }
  }

  const tabs = [
    { key: 'ingredients', label: `Ingredients${ingredients.length ? ` (${ingredients.length})` : ''}` },
    { key: 'steps', label: `Steps${steps.length ? ` (${steps.length})` : ''}` },
    { key: 'tips', label: `Tips${tips.length ? ` (${tips.length})` : ''}` },
  ];

  return (
    <div className="nmcr-root">
      <div className="nmcr-header">
        <button type="button" className="nmcr-close-btn" onClick={() => navigate(-1)} disabled={isLoading}>
          <span className="material-symbols-outlined">close</span>
        </button>
        <h1 className="nmcr-title">New recipe</h1>
        <button type="button" className="nmcr-post-btn" onClick={handlePost} disabled={isLoading}>
          {isLoading ? '…' : 'Post'}
        </button>
      </div>

      <div className="nmcr-body">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          style={{ display: 'none' }}
          onChange={handleMediaChange}
        />
        <div className="nmcr-media-picker" onClick={() => !mediaFile && fileInputRef.current?.click()}>
          {mediaFile ? (
            <>
              {(mediaIsVideo ? thumbPreviewUrl : mediaPreviewUrl) && (
                <img src={mediaIsVideo ? thumbPreviewUrl : mediaPreviewUrl} alt="" className="nmcr-media-thumb" />
              )}
              <div className="nmcr-media-overlay">
                <div className="nmcr-media-icon-circle">
                  <span className="material-symbols-outlined">{mediaIsVideo ? 'play_circle' : 'image'}</span>
                </div>
                <span className="nmcr-media-filename">{mediaFile.name}</span>
                <button type="button" className="nmcr-media-remove" onClick={(e) => { e.stopPropagation(); removeMedia(); }}>
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="nmcr-media-icon-circle">
                <span className="material-symbols-outlined">add_photo_alternate</span>
              </div>
              <span className="nmcr-media-placeholder">Tap to add a photo or video</span>
            </>
          )}
        </div>

        <div className="nmcr-field">
          <label className="nmcr-label">Recipe title *</label>
          <input
            type="text"
            className="nmcr-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Shorshe Ilish"
          />
        </div>

        <div className="nmcr-field">
          <label className="nmcr-label">Description *</label>
          <textarea
            className="nmcr-input nmcr-input-multi"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="What makes this recipe special?"
          />
        </div>

        <div className="nmcr-stats-row">
          <div className="nmcr-stat-field">
            <label className="nmcr-label">Cook time</label>
            <div className="nmcr-stat-input-wrap">
              <input type="number" min="0" value={cookTime} onChange={(e) => setCookTime(e.target.value)} placeholder="30" />
              <span className="nmcr-stat-unit">min</span>
            </div>
          </div>
          <div className="nmcr-stat-field">
            <label className="nmcr-label">Calories</label>
            <div className="nmcr-stat-input-wrap">
              <input type="number" min="0" value={calories} onChange={(e) => setCalories(e.target.value)} placeholder="0" />
              <span className="nmcr-stat-unit">kcal</span>
            </div>
          </div>
          <div className="nmcr-stat-field">
            <label className="nmcr-label">Servings</label>
            <div className="nmcr-stat-input-wrap">
              <input type="number" min="0" value={servings} onChange={(e) => setServings(e.target.value)} placeholder="1" />
              <span className="nmcr-stat-unit">ppl</span>
            </div>
          </div>
        </div>

        <div className="nmcr-field">
          <label className="nmcr-label">Cuisine type</label>
          {cuisinesLoading ? (
            <p className="nmcr-loading-text">Loading…</p>
          ) : (
            <div className="nmcr-cuisine-wrap">
              {cuisines.map((c) => {
                const active = selectedCuisines.includes(c.id);
                return (
                  <button
                    type="button"
                    key={c.id}
                    className={`nmcr-cuisine-pill${active ? ' active' : ''}`}
                    onClick={() => toggleCuisine(c.id)}
                  >
                    {c.emoji ? `${c.emoji} ${c.label}` : c.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="nmcr-tabbar">
          {tabs.map((tab) => (
            <button
              type="button"
              key={tab.key}
              className={`nmcr-tab-item${activeTab === tab.key ? ' active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="nmcr-tab-content">
          {activeTab === 'ingredients' && (
            <div>
              <div className="nmcr-ing-header-row">
                <span className="nmcr-label" style={{ flex: '0 0 30%' }}>Amount</span>
                <span className="nmcr-label" style={{ flex: 1 }}>Ingredient</span>
                <span style={{ width: 32 }} />
              </div>
              {ingredients.map((ing) => (
                <div key={ing.id} className="nmcr-row">
                  <input
                    className="nmcr-input"
                    style={{ flex: '0 0 30%' }}
                    value={ing.amount}
                    onChange={(e) => updateIngredient(ing.id, 'amount', e.target.value)}
                    placeholder="500g"
                  />
                  <input
                    className="nmcr-input"
                    style={{ flex: 1 }}
                    value={ing.item}
                    onChange={(e) => updateIngredient(ing.id, 'item', e.target.value)}
                    placeholder="e.g. Hilsa fish"
                  />
                  <button type="button" className="nmcr-remove-btn" onClick={() => removeIngredient(ing.id)}>
                    <span className="material-symbols-outlined">close</span>
                  </button>
                </div>
              ))}
              <button type="button" className="nmcr-add-row-btn" onClick={addIngredientRow}>
                <span className="material-symbols-outlined">add</span> Add ingredient
              </button>
            </div>
          )}

          {activeTab === 'steps' && (
            <div>
              {steps.map((step, i) => (
                <div key={step.id} className="nmcr-row nmcr-row-top">
                  <div className="nmcr-step-number">{i + 1}</div>
                  <textarea
                    className="nmcr-input nmcr-step-input"
                    value={step.instruction}
                    onChange={(e) => updateStep(step.id, e.target.value)}
                    placeholder="Describe this step…"
                  />
                  <button type="button" className="nmcr-remove-btn" onClick={() => removeStep(step.id)}>
                    <span className="material-symbols-outlined">close</span>
                  </button>
                </div>
              ))}
              <button type="button" className="nmcr-add-row-btn" onClick={addStepRow}>
                <span className="material-symbols-outlined">add</span> Add step
              </button>
            </div>
          )}

          {activeTab === 'tips' && (
            <div>
              {tips.map((tip) => (
                <div key={tip.id} className="nmcr-row">
                  <span className="material-symbols-outlined nmcr-tip-icon">lightbulb</span>
                  <input
                    className="nmcr-input"
                    style={{ flex: 1 }}
                    value={tip.tip_text}
                    onChange={(e) => updateTip(tip.id, e.target.value)}
                    placeholder="Share a helpful tip…"
                  />
                  <button type="button" className="nmcr-remove-btn" onClick={() => removeTip(tip.id)}>
                    <span className="material-symbols-outlined">close</span>
                  </button>
                </div>
              ))}
              <button type="button" className="nmcr-add-row-btn" onClick={addTipRow}>
                <span className="material-symbols-outlined">add</span> Add tip
              </button>
            </div>
          )}
        </div>
      </div>

      {isLoading && (
        <div className="nmcr-overlay">
          <div className="nmcr-overlay-card">
            <div className="nmcr-overlay-icon">
              <span className="material-symbols-outlined">restaurant</span>
            </div>
            <p className="nmcr-overlay-status">{uploadStatus || 'Preparing…'}</p>
            <p className="nmcr-overlay-hint">Don't close this screen</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateRecipePage;
